import { useState, useMemo, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { authedFetch } from '../../api/platformTenants';
import { PasswordGenerator } from './PasswordGenerator';
import { VpSelect } from '../ui/VpSelect';
import { IANA_TIMEZONES, DEFAULT_TIMEZONE, type IanaTimezone } from '../../constants/ianaTimezones';

interface Props { onSuccess: (tenantId: number) => void; }

type Step = 1 | 2 | 3;

export function TenantOnboardingWizard({ onSuccess }: Props) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const [step, setStep] = useState<Step>(1);
  // One stable key per POST. The three requests (tenant / warehouse / admin)
  // have different paths + bodies; reusing a single key makes the backend
  // IdempotencyFilter reject step 2 with "Idempotency-Key already used with a
  // different request body". Stable-per-step so a network retry still replays.
  const [idemKeys] = useState(() => ({
    tenant: crypto.randomUUID(),
    warehouse: crypto.randomUUID(),
    admin: crypto.randomUUID(),
  }));

  // Step 1
  const [tName, setTName] = useState('');
  const [tCode, setTCode] = useState('');
  const [tCodeManual, setTCodeManual] = useState(false);
  // Step 2
  const [whCode, setWhCode] = useState('');
  const [whName, setWhName] = useState('');
  const [whTimezone, setWhTimezone] = useState(DEFAULT_TIMEZONE);
  const [whAddress, setWhAddress] = useState('');
  // Step 3
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [passwordTemp, setPasswordTemp] = useState('');

  const [tenantId, setTenantId] = useState<number | null>(null);
  const [whCreated, setWhCreated] = useState(false);
  const [adminConflictKcSub, setAdminConflictKcSub] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const derivedCode = useMemo(() => {
    return tName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);
  }, [tName]);
  const effectiveCode = tCodeManual ? tCode : derivedCode;

  const codeRegex = /^[a-z][a-z0-9-]+$/;
  const whCodeRegex = /^[A-Z][A-Z0-9-]+$/;

  const step1Valid = tName.trim() !== '' && codeRegex.test(effectiveCode);
  const step2Valid = whCodeRegex.test(whCode) && whName.trim() !== '';
  const step3Valid = /^[^@]+@[^@]+$/.test(email) && displayName.trim() !== '' && passwordTemp.length >= 12;

  async function postStep1(): Promise<number> {
    const data = await authedFetch('/admin/platform/tenants', token, {
      method: 'POST',
      body: JSON.stringify({ code: effectiveCode, name: tName }),
      headers: { 'Idempotency-Key': idemKeys.tenant },
    }) as { id: number };
    return data.id;
  }

  async function postStep2(tid: number): Promise<void> {
    await authedFetch(`/admin/platform/tenants/${tid}/warehouses`, token, {
      method: 'POST',
      body: JSON.stringify({ code: whCode, name: whName, timezone: whTimezone, address: whAddress || null }),
      headers: { 'Idempotency-Key': idemKeys.warehouse },
    });
  }

  async function postStep3(tid: number, acceptExisting: boolean): Promise<void> {
    await authedFetch(`/admin/platform/tenants/${tid}/admins`, token, {
      method: 'POST',
      body: JSON.stringify({ email, display_name: displayName, password_temp: passwordTemp, accept_existing: acceptExisting }),
      headers: { 'Idempotency-Key': idemKeys.admin },
    });
  }

  async function submitFromStep3(acceptExisting = false) {
    setSubmitting(true); setError(null); setAdminConflictKcSub(null);
    try {
      let tid = tenantId;
      if (tid === null) { tid = await postStep1(); setTenantId(tid); }
      if (!whCreated) { await postStep2(tid); setWhCreated(true); }
      await postStep3(tid, acceptExisting);
      onSuccess(tid);
    } catch (e: any) {
      const status = e?.status;
      const body = e?.body;
      if (status === 409 && body?.code === 'email_exists' && body?.existing_kc_sub) {
        setAdminConflictKcSub(body.existing_kc_sub);
      } else {
        setError(body?.detail ?? e?.message ?? 'Errore sconosciuto');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <Stepper current={step} />

      {step === 1 && (
        <section>
          <h5 className="fs-16 mb-2">Step 1 — Tenant</h5>
          <FormGroup>
            <Label for="wiz-tname" className="mb-1">Nome</Label>
            <Input id="wiz-tname" value={tName} onChange={(e) => setTName(e.target.value)} maxLength={128} />
          </FormGroup>
          <FormGroup>
            <Label for="wiz-tcode" className="mb-1">Code</Label>
            <Input
              id="wiz-tcode"
              value={effectiveCode}
              onChange={(e) => { setTCodeManual(true); setTCode(e.target.value); }}
              maxLength={32}
            />
            {!codeRegex.test(effectiveCode) && effectiveCode.length > 0 &&
              <small>Code deve iniziare con lettera minuscola e contenere solo a-z, 0-9, dash.</small>}
          </FormGroup>
          <Actions>
            <Button type="button" color="primary" size="sm"
                    disabled={!step1Valid} onClick={() => setStep(2)}>Avanti →</Button>
          </Actions>
        </section>
      )}

      {step === 2 && (
        <section>
          <h5 className="fs-16 mb-2">Step 2 — Primo warehouse</h5>
          <FormGroup>
            <Label for="wiz-whcode" className="mb-1">Code</Label>
            <Input id="wiz-whcode" value={whCode} onChange={(e) => setWhCode(e.target.value.toUpperCase())} maxLength={32} />
          </FormGroup>
          <FormGroup>
            <Label for="wiz-whname" className="mb-1">Nome warehouse</Label>
            <Input id="wiz-whname" value={whName} onChange={(e) => setWhName(e.target.value)} maxLength={128} />
          </FormGroup>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            <span className="form-label mb-0">Timezone</span>
            <VpSelect
              ariaLabel="Timezone"
              options={IANA_TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
              value={whTimezone}
              onChange={(v) => setWhTimezone(String(v ?? DEFAULT_TIMEZONE) as IanaTimezone)}
            />
          </label>
          <FormGroup className="mt-2">
            <Label for="wiz-whaddr" className="mb-1">Indirizzo (opzionale)</Label>
            <Input id="wiz-whaddr" type="textarea" value={whAddress} onChange={(e) => setWhAddress(e.target.value)} maxLength={500} rows={2} />
          </FormGroup>
          <Actions>
            <Button type="button" color="light" size="sm" onClick={() => setStep(1)}>← Indietro</Button>
            <Button type="button" color="primary" size="sm"
                    disabled={!step2Valid} onClick={() => setStep(3)}>Avanti →</Button>
          </Actions>
        </section>
      )}

      {step === 3 && (
        <section>
          <h5 className="fs-16 mb-2">Step 3 — Tenant-admin iniziale</h5>
          <FormGroup>
            <Label for="wiz-email" className="mb-1">Email</Label>
            <Input id="wiz-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="wiz-display" className="mb-1">Display name</Label>
            <Input id="wiz-display" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={128} />
          </FormGroup>
          <FormGroup>
            <Label for="wiz-password" className="mb-1">Password temporanea</Label>
            <div className="d-flex gap-2">
              <Input id="wiz-password" type="text" value={passwordTemp} onChange={(e) => setPasswordTemp(e.target.value)}
                     minLength={12} style={{ flex: 1 }} />
              <PasswordGenerator onGenerated={setPasswordTemp} value={passwordTemp} />
            </div>
          </FormGroup>

          {adminConflictKcSub && (
            <div role="alert" className="alert alert-warning py-2 px-3 mb-0" style={{ marginTop: 12 }}>
              <p>L'email <code>{email}</code> esiste già nel sistema (id: {adminConflictKcSub}).
                Vuoi dargli accesso a questa azienda?</p>
              <Button type="button" color="primary" size="sm"
                      onClick={() => submitFromStep3(true)}>Dagli accesso a questa azienda</Button>
            </div>
          )}

          {error && <p className="text-danger small mb-0" style={{ marginTop: 12 }}>{error}</p>}

          <Actions>
            <Button type="button" color="light" size="sm"
                    onClick={() => setStep(2)} disabled={submitting}>← Indietro</Button>
            <Button type="button" color="primary" size="sm"
                    disabled={!step3Valid || submitting}
                    onClick={() => submitFromStep3(false)}>Crea tenant</Button>
          </Actions>
        </section>
      )}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
      {[1, 2, 3].map((n) => (
        <div key={n} style={{
          flex: 1, padding: '8px 12px', textAlign: 'center', borderBottom: '3px solid',
          borderColor: n <= current ? 'var(--vp-primary)' : 'var(--vp-border)',
          color: n <= current ? 'var(--vp-text)' : 'var(--vp-text-3)',
          fontWeight: n === current ? 600 : 400,
        }}>
          Step {n} / 3
        </div>
      ))}
    </div>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>{children}</div>;
}
